import {Area} from 'react-easy-crop';
import Api from '../../lib/apiClient';
import {clearEntityCache} from './CRUD';
import {AxiosProgressEvent} from 'axios';
import {MutableRefObject} from 'react';
import {SVGSpec} from '../../components/Admin/Form/Picture/svgTypes.ts';

export async function fileToBlob(file: File): Promise<Blob> {
    return new Blob([await file.arrayBuffer()], {type: file.type});
}

type UploadFormProps = {
    file: File;
    area?: Area;
    svgSpec?: SVGSpec | undefined;
    fileKey?: string;
};

export async function createFileFormData({
    file,
    svgSpec,
    fileKey = 'file',
    area,
}: UploadFormProps): Promise<FormData> {
    const form = new FormData();
    // Converts the file to a stable Blob to ensure its binary data is fully loaded before upload,
    // which is crucial for "virtual" files from remote sources like Google Drive picker on mobile.
    form.append(fileKey, await fileToBlob(file), file.name);

    if (area) {
        form.append('area', JSON.stringify(area));
    }
    if (svgSpec) {
        form.append('svgSpec', JSON.stringify(svgSpec));
    }

    return form;
}

export type FileUploadActionProps = {
    file: File;
    area?: Area;
    svgSpec?: SVGSpec | undefined;
    dontCaptureErrorRef?: MutableRefObject<boolean>;
    onProgress?: (progress: number) => void;
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
};

export type TypedUploadActionProps = {
    type: ImageType;
    id: string;
} & FileUploadActionProps;

type EntityUploadActionProps = {
    entity: string;
} & TypedUploadActionProps;

export async function uploadEntityFile<T>({
    entity,
    type,
    id,
    ...props
}: EntityUploadActionProps): Promise<T> {
    const res = await uploadImageByPath(`/${entity}/${id}/${type}`, props);

    clearEntityCache(entity, id);

    return res;
}

export async function deleteEntityFile<T>(
    entity: string,
    type: string,
    id: string,
): Promise<T> {
    const res = (await Api.put(`/${entity}/${id}/delete-${type}`)).data;
    clearEntityCache(entity, id);

    return res;
}

export async function uploadImage(
    imageType: ImageType,
    props: FileUploadActionProps,
) {
    return await uploadImageByPath(`/upload/${imageType}`, props);
}

async function uploadImageByPath(
    path: string,
    {onProgress, onUploadProgress, ...formProps}: FileUploadActionProps,
) {
    const form = await createFileFormData(formProps);

    return (
        await Api.post(path, form, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: progressEvent => {
                onUploadProgress?.(progressEvent);
                if (progressEvent.total) {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total,
                    );
                    onProgress?.(percentCompleted);
                }
            },
        })
    ).data;
}

export enum ImageType {
    Avatar = 'avatar',
    Logo = 'logo',
    Cover = 'cover',
    Background = 'background',
    AppClipHeader = 'app-clip-header',
    OpenGraph = 'open-graph',
    CampaignBanner = 'banner',
}
